import { useNavigation } from '@/app/hooks'
import { useAppSelector } from '@/hooks'
import { thermostatsCollection } from '@/reducers/thermostats'
import { vehicleChargersCollection } from '@/reducers/vehicleChargers'
import { useLocation, useParams } from 'react-router-dom'
import { usePrescribedChargers } from './usePrescribedChargers'
import { useInitConfigureDeviceFlow } from './useInitConfigureDeviceFlow'
import { consumePendingOnComplete } from './onCompleteRegistry'

export function useConfigureDeviceFlow() {
  const vehicles = useAppSelector((state) => state.vehicles.vehicles)
  const vehiclesLoading = useAppSelector((state) => state.vehicles.isLoading)
  const { data: thermostats, isLoadingOrIdle: thermostatsLoading } =
    thermostatsCollection.useFetch()
  const { data: chargers, isLoadingOrIdle: chargersLoading } =
    vehicleChargersCollection.useFetch()
  const { prescribedChargers, isLoading: prescribedChargersLoading } =
    usePrescribedChargers(chargers ?? [])
  const navigate = useNavigation()
  const location = useLocation()
  const { deviceId } = useParams<{ deviceId: string }>()
  const deviceIdsSearchParam = new URLSearchParams(location.search).get(
    'deviceIds',
  )
  const deviceIds = deviceIdsSearchParam ? deviceIdsSearchParam.split(',') : []
  const isLoading =
    vehiclesLoading ||
    thermostatsLoading ||
    chargersLoading ||
    prescribedChargersLoading
  const getSteps = () => {
    if (isLoading) {
      return null
    }
    const vehicleSteps = (vehicles ?? []).map((vehicle) => ({
      deviceId: vehicle.id,
      deviceType: ConfigureDeviceType.Vehicle,
      url: `/configure-devices/${vehicle.id}/vehicle`,
    }))
    const thermostatSteps = thermostats.map((thermostat) => ({
      deviceId: thermostat.id,
      deviceType: ConfigureDeviceType.Thermostat,
      url: `/configure-devices/${thermostat.id}/thermostat`,
    }))
    // DEV-6023: chargers are only included when the program prescribes a goal
    // for that charger. `usePrescribedChargers` does the filtering.
    const chargerSteps = prescribedChargers.map((charger) => ({
      deviceId: charger.id,
      deviceType: ConfigureDeviceType.Charger,
      url: `/configure-devices/${charger.id}/charger`,
    }))
    const mobileStep = {
      deviceId: 'mobile',
      deviceType: ConfigureDeviceType.Mobile,
      url: `/configure-devices/mobile/push-notifications`,
    }

    const steps = [
      ...vehicleSteps,
      ...thermostatSteps,
      ...chargerSteps,
      mobileStep,
    ].filter((d) => deviceIds.includes(d.deviceId.toString()))

    return steps
  }

  const steps = getSteps()
  const totalSteps = steps?.length
  const currentStepIndex = steps?.findIndex(
    (s) => s.deviceId.toString() === deviceId,
  )
  const completedSteps = currentStepIndex === -1 ? undefined : currentStepIndex
  const progress =
    completedSteps !== undefined && totalSteps !== undefined
      ? { totalSteps, completedSteps }
      : null

  const nextStep = () => {
    if (currentStepIndex === undefined) {
      return
    }
    const nextStep = steps?.[currentStepIndex + 1]
    if (nextStep) {
      navigate.pushRetainingParams(nextStep.url)
      return
    }
    const onComplete = consumePendingOnComplete()
    if (onComplete) {
      onComplete()
      return
    }
    navigate.push('/devices')
  }

  const { initFlow } = useInitConfigureDeviceFlow()

  return { deviceIds, steps, progress, nextStep, initFlow }
}

export enum ConfigureDeviceType {
  Vehicle = 'vehicle',
  Thermostat = 'thermostat',
  Charger = 'charger',
  Mobile = 'mobile',
}
